import React, { useState, useEffect } from 'react';
import {
  Ticket, Plus, Search, Filter, Clock, User, AlertTriangle,
  CheckCircle, XCircle, MessageSquare, ChevronRight, RefreshCw,
  Phone, Bell, BellOff, Send, Loader2, ArrowLeft
} from 'lucide-react';
import api from '../services/api';
import SpaceBackground from '../components/layout/SpaceBackground';

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // New ticket form
  const [newSubject, setNewSubject] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPriority, setNewPriority] = useState('normal');
  const [creating, setCreating] = useState(false);

  // New message
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    loadTickets();
    loadStats();
  }, [statusFilter, priorityFilter, searchQuery]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await api.get(`/tickets?${params.toString()}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/tickets/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadTicketDetails = async (ticketId) => {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      setSelectedTicket(response.data);
    } catch (error) {
      console.error('Error loading ticket:', error);
    }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    if (!newSubject.trim() || !newDescription.trim()) return;

    setCreating(true);
    try {
      const response = await api.post('/tickets', {
        subject: newSubject,
        description: newDescription,
        priority: newPriority
      });

      setShowNewTicket(false);
      setNewSubject('');
      setNewDescription('');
      setNewPriority('normal');
      loadTickets();
      loadStats();
      loadTicketDetails(response.data.id);
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedTicket) return;

    setSendingMessage(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/messages`, {
        content: newMessage,
        is_internal: isInternal
      });

      setNewMessage('');
      loadTicketDetails(selectedTicket.id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await api.patch(`/tickets/${ticketId}`, { status });
      loadTickets();
      loadStats();
      if (selectedTicket?.id === ticketId) {
        loadTicketDetails(ticketId);
      }
    } catch (error) {
      console.error('Error updating ticket:', error);
    }
  };

  const updateTicketPriority = async (ticketId, priority) => {
    try {
      await api.patch(`/tickets/${ticketId}`, { priority });
      loadTickets();
      if (selectedTicket?.id === ticketId) {
        loadTicketDetails(ticketId);
      }
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      waiting_on_client: 'bg-gray-100 text-gray-800',
      waiting_on_third_party: 'bg-gray-100 text-gray-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-200 text-gray-600'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-600',
      normal: 'bg-blue-100 text-blue-700',
      high: 'bg-orange-100 text-orange-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex h-screen relative">
      <SpaceBackground />
      {/* Sidebar - Ticket List */}
      <div className={`${selectedTicket ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-96 bg-virgo-navy-800/80 backdrop-blur-sm border-r border-virgo-teal-800 relative z-10`}>
        {/* Header */}
        <div className="p-4 border-b border-virgo-teal-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Ticket className="text-cyan-500" size={24} />
              <h1 className="text-xl font-bold text-white">Support Tickets</h1>
            </div>
            <button
              onClick={() => setShowNewTicket(true)}
              className="p-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              <Plus size={20} />
            </button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-4 gap-2 mb-4">
              <div className="text-center p-2 bg-virgo-navy-700 rounded">
                <div className="text-lg font-bold text-white">{stats.total_open}</div>
                <div className="text-xs text-virgo-teal-400">Open</div>
              </div>
              <div className="text-center p-2 bg-red-900/30 rounded">
                <div className="text-lg font-bold text-red-400">{stats.urgent}</div>
                <div className="text-xs text-red-300">Urgent</div>
              </div>
              <div className="text-center p-2 bg-orange-900/30 rounded">
                <div className="text-lg font-bold text-orange-400">{stats.high}</div>
                <div className="text-xs text-orange-300">High</div>
              </div>
              <div className="text-center p-2 bg-virgo-navy-700 rounded">
                <div className="text-lg font-bold text-gray-400">{stats.unassigned}</div>
                <div className="text-xs text-virgo-teal-400">Unassigned</div>
              </div>
            </div>
          )}

          {/* Search & Filters */}
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-virgo-teal-500" size={16} />
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white text-sm focus:outline-none"
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="waiting_on_client">Waiting on Client</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="flex-1 px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white text-sm focus:outline-none"
              >
                <option value="">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </div>

        {/* Ticket List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="animate-spin text-cyan-500" size={24} />
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-virgo-teal-500">
              <Ticket size={32} className="mb-2" />
              <p>No tickets found</p>
            </div>
          ) : (
            <div className="divide-y divide-virgo-teal-800">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => loadTicketDetails(ticket.id)}
                  className={`p-4 cursor-pointer hover:bg-virgo-navy-700 transition-colors ${
                    selectedTicket?.id === ticket.id ? 'bg-virgo-navy-700' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-virgo-teal-400">
                          {ticket.ticket_number}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </div>
                      <h3 className="text-white font-medium mt-1 line-clamp-1">
                        {ticket.subject}
                      </h3>
                    </div>
                    <ChevronRight className="text-virgo-teal-500" size={16} />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`px-2 py-0.5 rounded-full ${getStatusColor(ticket.status)}`}>
                      {ticket.status.replace(/_/g, ' ')}
                    </span>
                    <span className="text-virgo-teal-500">
                      {formatDate(ticket.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Ticket Details */}
      <div className={`${selectedTicket ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-virgo-navy-900 relative z-10`}>
        {selectedTicket ? (
          <>
            {/* Ticket Header */}
            <div className="p-4 border-b border-virgo-teal-800 bg-virgo-navy-800">
              <div className="flex items-center gap-4 mb-3">
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="md:hidden p-2 text-virgo-teal-400 hover:text-white"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono text-virgo-teal-400">
                      {selectedTicket.ticket_number}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(selectedTicket.status)}`}>
                      {selectedTicket.status.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(selectedTicket.priority)}`}>
                      {selectedTicket.priority}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-white mt-1">
                    {selectedTicket.subject}
                  </h2>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <select
                  value={selectedTicket.status}
                  onChange={(e) => updateTicketStatus(selectedTicket.id, e.target.value)}
                  className="px-3 py-1.5 bg-virgo-navy-700 border border-virgo-teal-700 rounded text-white text-sm"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="waiting_on_client">Waiting on Client</option>
                  <option value="waiting_on_third_party">Waiting on Third Party</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <select
                  value={selectedTicket.priority}
                  onChange={(e) => updateTicketPriority(selectedTicket.id, e.target.value)}
                  className="px-3 py-1.5 bg-virgo-navy-700 border border-virgo-teal-700 rounded text-white text-sm"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Original Description */}
              <div className="bg-virgo-navy-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <User className="text-cyan-500" size={16} />
                  <span className="text-white font-medium">
                    {selectedTicket.created_by_name || 'Unknown'}
                  </span>
                  <span className="text-virgo-teal-500 text-sm">
                    {formatDate(selectedTicket.created_at)}
                  </span>
                </div>
                <p className="text-virgo-teal-300 whitespace-pre-wrap">
                  {selectedTicket.description}
                </p>
              </div>

              {/* Messages Thread */}
              {selectedTicket.messages?.map((message) => (
                <div
                  key={message.id}
                  className={`rounded-lg p-4 ${
                    message.is_internal
                      ? 'bg-yellow-900/20 border border-yellow-700/50'
                      : 'bg-virgo-navy-800'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {message.is_internal && (
                      <span className="text-xs bg-yellow-600 text-white px-2 py-0.5 rounded">
                        Internal Note
                      </span>
                    )}
                    <User className="text-cyan-500" size={16} />
                    <span className="text-white font-medium">
                      {message.author_name || 'System'}
                    </span>
                    <span className="text-virgo-teal-500 text-sm">
                      {formatDate(message.created_at)}
                    </span>
                    {message.source === 'sms' && (
                      <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">
                        SMS
                      </span>
                    )}
                  </div>
                  <p className="text-virgo-teal-300 whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
              ))}
            </div>

            {/* Reply Form */}
            <div className="p-4 border-t border-virgo-teal-800 bg-virgo-navy-800">
              <form onSubmit={sendMessage}>
                <div className="mb-2">
                  <label className="flex items-center gap-2 text-sm text-virgo-teal-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInternal}
                      onChange={(e) => setIsInternal(e.target.checked)}
                      className="rounded border-virgo-teal-700"
                    />
                    Internal note (not visible to client)
                  </label>
                </div>
                <div className="flex gap-2">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your reply..."
                    rows={2}
                    className="flex-1 px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sendingMessage}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingMessage ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <Send size={20} />
                    )}
                  </button>
                </div>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-virgo-teal-500">
            <div className="text-center">
              <Ticket size={48} className="mx-auto mb-4" />
              <p>Select a ticket to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-800 rounded-lg p-6 w-full max-w-lg mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Create New Ticket</h2>
            <form onSubmit={createTicket}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-virgo-teal-400 mb-1">Subject</label>
                  <input
                    type="text"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    placeholder="Brief description of the issue"
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-400 mb-1">Description</label>
                  <textarea
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Detailed description of your request..."
                    rows={4}
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-virgo-teal-400 mb-1">Priority</label>
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value)}
                    className="w-full px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white focus:outline-none"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewTicket(false)}
                  className="px-4 py-2 text-virgo-teal-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !newSubject.trim() || !newDescription.trim()}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-gray-600 transition-colors flex items-center gap-2"
                >
                  {creating && <Loader2 className="animate-spin" size={16} />}
                  Create Ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
